import React from 'react';

import { OvertimeApprovalDetailExpandedRow } from '@/components/features/overtime';
import { PermissionGuard } from '@/components/layout';
import PageLayout from '@/components/layout/PageLayout';
import { Banner } from '@/components/ui/banners';
import { LinkButton } from '@/components/ui/buttons';
import { Card, IndicatorCard } from '@/components/ui/cards';
import { CommentBox, Switch } from '@/components/ui/inputs';
import { Selector } from '@/components/ui/inputs/selector';
import Table from '@/components/ui/tables/Table';
import { OVERTIME_COUNTER_SLOTS } from '@/hooks/overtime/overtimeApprovalCounters';
import { useOvertimeApprovalDetail } from '@/hooks/overtime/useOvertimeApprovalDetail';
import { useTranslation } from '@/i18n';
import { normalizeNumericSelectValue } from '@/utils/formHelpers';
import {
  OVERTIME_COMMENT_MAX_LENGTH,
  OVERTIME_JUSTIFICATION_FREE_COMMENT_MAX_LENGTH,
} from '@/api/domains/overtime/overtimeApproval.types';
import type { OvertimeApprovalDetailRow } from '@/api/domains/overtime/overtimeApproval.types';


/**
 * Detalle de horas extras (ASIS-5355/5356/5357/5358), abierto desde el listado general de
 * Aprobación HHEE para uno o varios colaboradores. Es un wrapper delgado sobre
 * `useOvertimeApprovalDetail` — toda la lógica (edición de horas, selección, aprobación con
 * comentario, justificación del revisor, paginación) vive en el hook.
 *
 * Un solo layout para los dos perfiles: lo que cambia es el bloque sobre la tabla (comentario
 * obligatorio del aprobador vs. selector de tipo de justificación del revisor) y las columnas, que
 * las arma el hook.
 */
export const OvertimeApprovalDetail: React.FC = () => {
  const { t: translate } = useTranslation();
  const {
    rows,
    columns,
    getRowId,
    counters,
    onCounterSelect,
    isDecimalFormat,
    onHourFormatToggle,
    overtimeApprovalFlowEnabled,
    isLoading,
    bulkActions,
    selectedRowIds,
    onSelectionChange,
    closedCutMessage,
    canEditHours,
    canSubmit,
    isSubmitting,
    comment,
    onCommentChange,
    isCommentMissing,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    onSearch,
    onSortChange,
    isReviewer,
    detailTitle,
    justificationTypeOptions,
    hasNoJustificationTypes,
    isLoadingJustificationTypes,
    justificationTypeId,
    onJustificationTypeChange,
    isJustificationTypeMissing,
    requiresFreeComment,
    freeComment,
    onFreeCommentChange,
    isFreeCommentMissing,
  } = useOvertimeApprovalDetail();

  /* Los avisos exigidos por los criterios de aceptación se arman con dos claves ("Atención:" + el
     detalle), para no duplicar el literal en el diccionario. */
  const commentRequiredMessage = [
    translate('attendance:overtimeApproval.detail.approve.commentRequiredEmphasis'),
    translate('attendance:overtimeApproval.detail.approve.commentRequired'),
  ].join('');

  const justificationTypeRequiredMessage = [
    translate('attendance:overtimeApproval.detail.justify.typeRequiredEmphasis'),
    translate('attendance:overtimeApproval.detail.justify.typeRequired'),
  ].join('');

  const freeCommentRequiredMessage = [
    translate('attendance:overtimeApproval.detail.justify.freeCommentRequiredEmphasis'),
    translate('attendance:overtimeApproval.detail.justify.freeCommentRequired'),
  ].join('');

  /* Aviso permanente mientras el corte esté cerrado, idéntico al del listado general: además del
     mensaje, la acción de escritura queda deshabilitada y las horas dejan de ser editables. No es
     descartable a propósito. */
  const closedCutNotice = closedCutMessage && (
    <Banner
      variant="info"
      density="compact"
      rounded={false}
      className="px-4 md:px-8 lg:px-16"
      text={closedCutMessage}
      data-testid="overtime-approval-detail-closed-cut-message"
    />
  );

  /* Bloque del revisor: ocupa el lugar exacto del comentario del aprobador. Sin modal — el detalle
     ya es una pantalla completa, así que el selector vive in-page. El campo libre aparece solo
     cuando el tipo elegido lo exige (código OTRO, resuelto por el backend en `requiresFreeComment`,
     nunca por el texto de la etiqueta). Con el catálogo vacío el selector queda sin opciones y la
     acción Justificar deshabilitada: nunca se envía una justificación sin tipo. */
  const justificationBlock = (
    <>
      <Card
        elevation={1}
        padding="24px 16px"
        className="w-full"
        data-testid="overtime-approval-detail-justification-card"
      >
        <Selector
          /* El selector se acota al ancho del texto largo del catálogo; la Card sigue ocupando
             todo el ancho de la pantalla. Sin esto el desplegable se estiraba de borde a borde. */
          className="w-full max-w-[280px]"
          label={translate('attendance:overtimeApproval.detail.justify.typeLabel')}
          required
          options={justificationTypeOptions}
          value={justificationTypeId !== null ? String(justificationTypeId) : ''}
          /* `normalizeNumericSelectValue` devuelve `undefined` al limpiar; el hook modela la
             ausencia de tipo como `null`, que es lo que la validación de Justificar consulta. */
          onChange={(value) => onJustificationTypeChange(
            normalizeNumericSelectValue(value) ?? null,
          )}
          isLoading={isLoadingJustificationTypes}
          disabled={!canSubmit || isSubmitting}
          placeholder={hasNoJustificationTypes
            ? translate('attendance:overtimeApproval.detail.justify.typeEmpty')
            : translate('attendance:overtimeApproval.detail.justify.typePlaceholder')}
          color={isJustificationTypeMissing ? 'error' : 'default'}
          supportingText={isJustificationTypeMissing
            ? justificationTypeRequiredMessage
            : undefined}
          data-testid="overtime-approval-detail-justification-type-selector"
        />
      </Card>

      {requiresFreeComment && (
        <CommentBox
          value={freeComment}
          onChange={onFreeCommentChange}
          placeholder={translate('attendance:overtimeApproval.detail.justify.freeCommentPlaceholder')}
          ariaLabel={translate('attendance:overtimeApproval.detail.justify.freeCommentAriaLabel')}
          required
          disabled={!canSubmit || isSubmitting}
          maxLength={OVERTIME_JUSTIFICATION_FREE_COMMENT_MAX_LENGTH}
          color={isFreeCommentMissing ? 'error' : 'default'}
          supportingText={isFreeCommentMissing ? freeCommentRequiredMessage : undefined}
          data-testid="overtime-approval-detail-free-comment-input"
          cardTestId="overtime-approval-detail-free-comment-card"
        />
      )}
    </>
  );

  /* Comentario obligatorio de la aprobación. Vive sobre la tabla y no en un modal: se escribe antes
     de seleccionar y aprobar, y al presionar Aprobar sin texto el propio input muestra el aviso en
     rojo. */
  const approvalCommentBlock = (
    <CommentBox
      value={comment}
      onChange={onCommentChange}
      placeholder={translate('attendance:overtimeApproval.detail.approve.commentPlaceholder')}
      ariaLabel={translate('attendance:overtimeApproval.detail.approve.commentAriaLabel')}
      required
      /* Mismo bloqueo que la columna de horas y que Aprobar: un corte cerrado (o la falta de
         permiso de edición) no admite modificaciones. Durante el envío se bloquea junto con la
         tabla — el comentario ya viaja en la petición. */
      disabled={!canEditHours || isSubmitting}
      maxLength={OVERTIME_COMMENT_MAX_LENGTH}
      color={isCommentMissing ? 'error' : 'default'}
      supportingText={isCommentMissing ? commentRequiredMessage : undefined}
      data-testid="overtime-approval-detail-comment-input"
      cardTestId="overtime-approval-detail-comment-card"
    />
  );

  return (
    <PageLayout banner={closedCutNotice} dynamicTitle={detailTitle}>
      <div className="flex flex-col gap-6" data-testid="overtime-approval-detail-page">
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-end gap-4">
            <Switch
              checked={isDecimalFormat}
              onChange={onHourFormatToggle}
              label={translate('attendance:overtimeApproval.decimalSwitch.label')}
              tooltipText={translate('attendance:overtimeApproval.decimalSwitch.tooltip')}
              data-testid="overtime-approval-detail-decimal-switch"
            />
          </div>

          <div
            className="flex flex-row flex-wrap gap-4"
            data-testid="overtime-approval-detail-counters"
          >
            {counters.map((counter) => (
              <IndicatorCard
                key={counter.key}
                label={counter.label}
                value={counter.value}
                padValue={false}
                tooltipText={counter.tooltip}
                variant={counter.variant}
                isSelected={counter.isSelected}
                onClick={() => onCounterSelect(counter.key)}
                data-testid={`overtime-approval-detail-counter-${counter.key}`}
              />
            ))}
            {/* Las tarjetas son `flex-1`, así que un set corto (el revisor ve tres) se estiraría
                hasta llenar la fila. Los espaciadores ocupan los lugares sobrantes para que el
                hueco quede al final y el ancho de cada tarjeta no dependa del perfil. */}
            {Array.from(
              { length: Math.max(0, OVERTIME_COUNTER_SLOTS - counters.length) },
              (_unused, index) => (
                <div
                  key={`overtime-approval-detail-counter-slot-${index}`}
                  className="flex-1 min-w-[180px]"
                  aria-hidden="true"
                />
              ),
            )}
          </div>
        </div>

        {isReviewer ? justificationBlock : approvalCommentBlock}

        {/* Sobre la tabla, alineado a la derecha y tras el mismo PermissionGuard que el listado
            general. Habilitado a propósito aunque el endpoint de exportación aún no exista: el
            tooltip declara que la acción está pendiente y el clic no hace nada. */}
        <PermissionGuard action="view">
          <div className="flex justify-end">
            <span title={translate('attendance:overtimeApproval.detail.pendingDevelopment')}>
              <LinkButton
                iconLeft="import"
                onClick={() => undefined}
                data-testid="overtime-approval-detail-download-button"
              >
                {translate('common:actions.download')}
              </LinkButton>
            </span>
          </div>
        </PermissionGuard>

        <Table<OvertimeApprovalDetailRow>
          columns={columns}
          data={rows}
          totalRows={total}
          /* Durante el envío la tabla queda bloqueada: la operación reescribe estas mismas filas,
             así que seguir editando horas o cambiando la selección mientras viaja la petición
             operaría sobre datos que están por reemplazarse. */
          isLoading={isLoading || isSubmitting}
          emptyMessage={translate('attendance:overtimeApproval.detail.emptyMessage')}
          /* Búsqueda, orden y paginación server-side (ASIS-5358), igual que el listado general: el
             universo del corte puede tener cientos de jornadas aunque venga acotado por
             employeeIds. */
          serverSideSort
          onSortChange={onSortChange}
          onSearch={onSearch}
          searchPlaceholder={translate('common:table.searchPlaceholder')}
          /* Sin <PermissionGuard> aquí a propósito: Table es genérico (Regla 8) y no tiene un slot
             para envolver solo su columna de selección. La habilitación real de las acciones y de
             la edición de horas ya deriva de can('edit') dentro del hook, y el backend vuelve a
             chequearlo. */
          selectable
          getRowId={getRowId}
          bulkActions={bulkActions}
          selectedRowIds={selectedRowIds}
          onSelectionChange={onSelectionChange}
          pagination={{
            page,
            pageSize,
            total,
            onChange: setPage,
            onPageSizeChange: setPageSize,
          }}
          expandable={{
            showExpandColumn: true,
            highlightBackground: false,
            position: 'right',
            renderExpanded: (row) => (
              <OvertimeApprovalDetailExpandedRow
                row={row}
                overtimeApprovalFlowEnabled={overtimeApprovalFlowEnabled}
              />
            ),
          }}
          data-testid="overtime-approval-detail-table"
        />
      </div>
    </PageLayout>
  );
};

export default OvertimeApprovalDetail;
